import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft } from 'lucide-react';
import type { Candidate, PipelineStage } from '@/models/job';
import { Button, type ButtonSize } from '@/components/ui/atoms/Button';
import { DropdownMenu } from '@/components/ui/organisms/DropdownMenu';
import { getStageMoveTargets } from '@/utils/candidate-actions';
import { useStageDisplayLabel } from '@/hooks/useStageDisplayLabel';

interface CandidateMoveToMenuProps {
  candidate: Candidate;
  stages: PipelineStage[];
  onMove: (stage: PipelineStage, fromPosition?: number) => void;
  disabled?: boolean;
  size?: ButtonSize;
}

/** "Move to" icon button + stage list; the parent owns the shared stage move. */
export function CandidateMoveToMenu({
  candidate,
  stages,
  onMove,
  disabled,
  size = 'sm',
}: CandidateMoveToMenuProps) {
  const { t } = useTranslation('candidates');
  const getStageLabel = useStageDisplayLabel();
  const { fromPosition, targets } = useMemo(
    () => getStageMoveTargets(candidate, stages),
    [candidate, stages]
  );

  if (targets.length === 0) return null;

  const label = t('tooltips.move_to');

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button
          type="icon"
          intent="muted"
          size={size}
          icon={<ArrowRightLeft />}
          title={label}
          aria-label={label}
          disabled={disabled}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className="min-w-[160px]">
        <DropdownMenu.Label>{label}</DropdownMenu.Label>
        {targets.map(({ stage, isAllowed }) => (
          <DropdownMenu.Item
            key={stage.id}
            disabled={!isAllowed}
            onClick={() => onMove(stage, fromPosition)}
          >
            {getStageLabel(stage)}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
